import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin } from "lucide-react";

const NAV = [
  {
    heading: "Explore",
    links: [
      ["Solutions", "#services"],
      ["The Method", "#method"],
      ["The Scorecard", "#assessment-pitch"],
      ["Start the Scorecard", "/assessment"],
    ],
  },
  {
    heading: "Prospera",
    links: [
      ["Meet Biagio", "#about"],
      ["Client testimonials", "#testimonials"],
      ["Frequently asked", "#faq"],
      ["Book a consultation", "#contact"],
    ],
  },
  {
    heading: "Legal & Regulatory",
    links: [
      ["Privacy Policy", "/legal/privacy"],
      ["Terms of Service", "/legal/terms"],
      ["Regulatory Disclosures", "/legal/disclosures"],
      ["Accessibility (AODA)", "/legal/accessibility"],
      ["Complaints & Escalation", "/legal/complaints"],
    ],
  },
];

export default function Footer() {
  return (
    <footer
      className="relative pt-20 pb-10 overflow-hidden"
      style={{ background: "#0B1F3F" }}
      data-testid="footer"
    >
      <div className="max-w-[1280px] mx-auto px-6 lg:px-12 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr_1fr_1fr] gap-10 pb-14 border-b border-white/[0.06]">
          <div>
            <div className="flex items-center gap-3">
              <img
                src="/prospera-icon.png"
                alt=""
                className="w-11 h-11 rounded-full ring-1 ring-amber-300/30"
              />
              <div>
                <div className="font-display text-white text-xl">
                  Prospera Financial Inc.
                </div>
                <div className="text-[10px] tracking-[0.35em] uppercase text-[#D9BE8A] mt-1">
                  Independent · Toronto
                </div>
              </div>
            </div>
            <p className="text-white/55 text-sm mt-6 max-w-md leading-relaxed">
              A private advisory practice serving Canadian families, executives
              and business owners. Insurance and investments arranged
              independently, on a fiduciary basis.
            </p>
            <div className="mt-6 space-y-2.5 text-sm text-white/65">
              <a
                href="tel:+14165550102"
                className="flex items-center gap-3 hover:text-amber-200"
              >
                <Phone size={14} className="text-amber-300/70" /> +1 (416) 555-0102
              </a>
              <a
                href="mailto:info@prosperafinancial.ca"
                className="flex items-center gap-3 hover:text-amber-200"
              >
                <Mail size={14} className="text-amber-300/70" /> info@prosperafinancial.ca
              </a>
              <div className="flex items-center gap-3">
                <MapPin size={14} className="text-amber-300/70" /> Toronto,
                Ontario · By appointment
              </div>
            </div>
          </div>

          {NAV.map((col) => (
            <div key={col.heading}>
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/45 mb-5">
                {col.heading}
              </div>
              <ul className="space-y-3">
                {col.links.map(([label, href]) => (
                  <li key={label}>
                    {href.startsWith("#") || href.startsWith("mailto") ? (
                      <a
                        href={href}
                        className="text-white/75 text-sm hover:text-amber-200 transition"
                      >
                        {label}
                      </a>
                    ) : (
                      <Link
                        to={href}
                        className="text-white/75 text-sm hover:text-amber-200 transition"
                      >
                        {label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Regulatory strip */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/45 mb-4">
              Licensing & Regulation
            </div>
            <p className="text-white/55 text-xs leading-relaxed max-w-3xl">
              Prospera Financial Inc. is an Ontario corporation. Life,
              accident &amp; sickness insurance is arranged by licensed agents
              regulated by the{" "}
              <strong className="text-white/80">
                Financial Services Regulatory Authority of Ontario (FSRA)
              </strong>
              . Segregated fund and mutual fund business is conducted through
              carriers regulated by the{" "}
              <strong className="text-white/80">
                Canadian Investment Regulatory Organization (CIRO)
              </strong>{" "}
              under the oversight of the{" "}
              <strong className="text-white/80">
                Ontario Securities Commission (OSC)
              </strong>
              . Eligible client assets are protected by the{" "}
              <strong className="text-white/80">
                Canadian Investor Protection Fund (CIPF)
              </strong>{" "}
              within CIPF coverage limits. Advisors are members of{" "}
              <strong className="text-white/80">Advocis</strong>, the Financial
              Advisors Association of Canada.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-3 lg:justify-end">
            {["FSRA", "CIRO", "CIPF", "OSC", "ADVOCIS", "FP CANADA"].map(
              (b) => (
                <span
                  key={b}
                  className="text-[10px] uppercase tracking-[0.3em] text-white/45"
                >
                  {b}
                </span>
              )
            )}
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-white/[0.06] flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-white/40">
          <div className="flex items-center gap-5">
            <span>
              © {new Date().getFullYear()} Prospera Financial Inc. All rights reserved.
            </span>
            <span className="hidden md:inline text-white/20">·</span>
            <Link
              to="/login"
              data-testid="footer-admin-link"
              className="text-white/50 hover:text-amber-200 transition inline-flex items-center gap-1"
            >
              Advisor sign in
              <span aria-hidden>→</span>
            </Link>
          </div>
          <div className="max-w-2xl leading-relaxed">
            This website is for informational purposes only and does not
            constitute financial, legal or tax advice. Insurance product
            recommendations are subject to underwriting approval and issuance
            of a policy contract. Investment returns are not guaranteed.
          </div>
        </div>
      </div>
    </footer>
  );
}
